<template>
  <base-layout :full-width="true">
    <div
      class="bg-white/80 backdrop-blur-xl relative isolate overflow-hidden rounded-2xl shadow-[0_10px_30px_rgba(0,0,0,0.06)]"
    >
      <div class="absolute inset-0 -z-10">
        <div
          class="absolute top-0 left-1/2 -translate-x-1/2 w-full h-96 bg-gradient-to-b from-blue-50/40 via-green-50/30 to-transparent"
        ></div>
        <div
          class="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-100/20 to-transparent rounded-full blur-3xl"
        ></div>
        <div
          class="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-tr from-green-100/20 to-transparent rounded-full blur-3xl"
        ></div>
        <div class="absolute inset-0 bg-grid-pattern opacity-[0.015]"></div>
        <div
          class="absolute inset-0 pointer-events-none [background:radial-gradient(rgba(255,255,255,0.6)_1px,transparent_1px)] [background-size:20px_20px] opacity-[0.02]"
        ></div>
        <div
          class="absolute -top-16 left-1/4 w-[480px] h-[480px] bg-gradient-to-br from-cyan-100/30 via-blue-100/20 to-transparent rounded-full blur-3xl opacity-60"
        ></div>
      </div>

      <div
        class="mx-auto max-w-6xl px-6 sm:px-8 lg:px-10 pt-8 lg:pt-12 pb-10 flex flex-col gap-8"
      >
        <div class="mb-0">
          <div class="lg:hidden">
            <div class="text-center mb-6">
              <div class="flex items-center justify-center gap-2 mb-3">
                <div
                  class="w-2.5 h-2.5 rounded-full"
                  :class="overallStatusDotClass"
                ></div>
                <h1 class="text-2xl font-semibold text-gray-900">
                  {{ t("status.title") }}
                </h1>
              </div>

              <div class="flex items-center justify-center gap-4 mb-4">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
                  :class="overallStatusBadgeClass"
                >
                  {{ overallStatusText }}
                </span>
                <button
                  @click="fetchStatus"
                  :disabled="isRefreshing"
                  class="inline-flex items-center gap-2 px-3.5 py-1.5 text-xs font-medium text-gray-700 bg-white/80 border border-gray-200 rounded-full hover:border-gray-300 hover:bg-white transition-all duration-200 disabled:opacity-60 shadow-[0_1px_2px_rgba(0,0,0,0.04)] active:scale-[0.98]"
                >
                  <ArrowPathIcon
                    class="w-3.5 h-3.5"
                    :class="{ 'animate-spin': isRefreshing }"
                  />
                  <span>{{ t("common.refresh") }}</span>
                </button>
              </div>

              <p class="text-sm text-gray-500">
                {{ t("status.lastUpdate") }}: {{ lastUpdateTime }}
              </p>
            </div>

            <div class="grid grid-cols-3 gap-3 mb-8">
              <div
                class="rounded-xl bg-white/60 p-3 text-center ring-1 ring-inset ring-gray-900/10 backdrop-blur-sm"
              >
                <div
                  class="flex h-9 w-9 mx-auto items-center justify-center rounded-lg bg-green-500/10"
                >
                  <ShieldCheckIcon class="h-6 w-6 text-green-600" />
                </div>
                <dd
                  class="mt-2 text-lg font-semibold tracking-tight text-gray-900"
                >
                  {{ operationalCount }}/{{ totalSystems }}
                </dd>
                <dt class="mt-1 truncate text-[11px] font-medium text-gray-600">
                  {{ t("status.systemsOperational") }}
                </dt>
              </div>
              <div
                class="rounded-xl bg-white/60 p-3 text-center ring-1 ring-inset ring-gray-900/10 backdrop-blur-sm"
              >
                <div
                  class="flex h-9 w-9 mx-auto items-center justify-center rounded-lg bg-blue-500/10"
                >
                  <ClockIcon class="h-6 w-6 text-blue-600" />
                </div>
                <dd
                  class="mt-2 text-lg font-semibold tracking-tight text-gray-900"
                >
                  {{ averageLatency }}<span class="text-base">ms</span>
                </dd>
                <dt class="mt-1 truncate text-[11px] font-medium text-gray-600">
                  {{ t("status.averageLatency") }}
                </dt>
              </div>
              <div
                class="rounded-xl bg-white/60 p-3 text-center ring-1 ring-inset ring-gray-900/10 backdrop-blur-sm"
              >
                <div
                  class="flex h-9 w-9 mx-auto items-center justify-center rounded-lg bg-yellow-500/10"
                >
                  <ChartBarIcon class="h-6 w-6 text-yellow-600" />
                </div>
                <dd
                  class="mt-2 text-lg font-semibold tracking-tight text-gray-900"
                >
                  {{ averageUptime }}
                </dd>
                <dt class="mt-1 truncate text-[11px] font-medium text-gray-600">
                  {{ t("status.averageUptime") }}
                </dt>
              </div>
            </div>
          </div>

          <div class="hidden lg:flex lg:items-center lg:justify-between mb-0">
            <div class="flex items-center gap-6">
              <div class="flex items-center gap-3">
                <div
                  class="w-3 h-3 rounded-full"
                  :class="overallStatusDotClass"
                ></div>
                <h1 class="text-3xl font-semibold text-gray-900">
                  {{ t("status.title") }}
                </h1>
              </div>

              <div class="flex items-center gap-4">
                <span
                  class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border"
                  :class="overallStatusBadgeClass"
                >
                  {{ overallStatusText }}
                </span>
                <button
                  @click="fetchStatus"
                  :disabled="isRefreshing"
                  class="inline-flex items-center gap-2 px-3.5 py-1.5 text-sm font-medium text-gray-700 bg-white/80 border border-gray-200 rounded-full hover:border-gray-300 hover:bg-white transition-all duration-200 disabled:opacity-60 shadow-[0_1px_2px_rgba(0,0,0,0.04)] active:scale-[0.98]"
                >
                  <ArrowPathIcon
                    class="w-4 h-4"
                    :class="{ 'animate-spin': isRefreshing }"
                  />
                  <span>{{ t("common.refresh") }}</span>
                </button>
              </div>

              <p class="text-sm text-gray-500 whitespace-nowrap">
                {{ t("status.lastUpdate") }}: {{ lastUpdateTime }}
              </p>
            </div>

            <div class="flex items-center gap-6">
              <div class="flex items-center gap-3">
                <div
                  class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-green-500/10"
                >
                  <ShieldCheckIcon class="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <dt class="truncate text-xs font-medium text-gray-600">
                    {{ t("status.systemsOperational") }}
                  </dt>
                  <dd
                    class="mt-1 text-xl font-semibold tracking-tight text-gray-900"
                  >
                    {{ operationalCount }}/{{ totalSystems }}
                  </dd>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-blue-500/10"
                >
                  <ClockIcon class="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <dt class="truncate text-xs font-medium text-gray-600">
                    {{ t("status.averageLatency") }}
                  </dt>
                  <dd
                    class="mt-1 text-xl font-semibold tracking-tight text-gray-900"
                  >
                    {{ averageLatency
                    }}<span class="text-base font-medium text-gray-500"
                      >ms</span
                    >
                  </dd>
                </div>
              </div>
              <div class="flex items-center gap-3">
                <div
                  class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-yellow-500/10"
                >
                  <ChartBarIcon class="h-6 w-6 text-yellow-600" />
                </div>
                <div>
                  <dt class="truncate text-xs font-medium text-gray-600">
                    {{ t("status.averageUptime") }}
                  </dt>
                  <dd
                    class="mt-1 text-xl font-semibold tracking-tight text-gray-900"
                  >
                    {{ averageUptime }}
                  </dd>
                </div>
              </div>
            </div>
          </div>

          <div
            class="h-px bg-gradient-to-r from-transparent via-gray-200/70 to-transparent mt-6"
          ></div>
        </div>

        <div class="flex-1">
          <div class="mb-6">
            <h2 class="text-lg font-semibold text-gray-900 mb-1">
              {{ t("status.systemDetails") }}
            </h2>
            <p class="text-sm text-gray-500">
              {{ t("status.timeline") }} - {{ t("status.last48Hours") }}
            </p>
          </div>

          <div v-if="loading" class="flex justify-center items-center py-12">
            <div class="flex items-center gap-2">
              <ArrowPathIcon class="w-4 h-4 animate-spin text-gray-400" />
              <span class="text-sm text-gray-500">{{
                t("common.loading")
              }}</span>
            </div>
          </div>

          <div v-else-if="systemList.length === 0" class="text-center py-12">
            <QuestionMarkCircleIcon
              class="w-12 h-12 text-gray-400 mx-auto mb-3"
            />
            <h3 class="text-base font-medium text-gray-900 mb-1">
              {{ t("status.noData") }}
            </h3>
            <p class="text-sm text-gray-500 max-w-sm mx-auto">
              {{ t("status.noDataDesc") }}
            </p>
          </div>

          <div v-else class="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div
              v-for="system in systemList"
              :key="system.id"
              class="relative flex h-full flex-col rounded-xl bg-white/60 p-3.5 ring-1 ring-inset ring-gray-900/10 backdrop-blur-sm transition-all duration-200 hover:-translate-y-0.5 hover:shadow-md hover:bg-white/80"
            >
              <div class="flex items-start justify-between gap-2">
                <div class="flex items-start gap-2.5 min-w-0">
                  <div
                    class="w-2 h-2 flex-shrink-0 rounded-full mt-1.5"
                    :class="getStatusDotClass(system.status)"
                  ></div>
                  <div class="min-w-0 flex-1">
                    <h3
                      class="text-sm font-semibold text-gray-900 leading-tight h-10 flex items-start pt-0.5"
                    >
                      {{ t(`services.${system.name}`) }}
                    </h3>
                  </div>
                </div>
                <span
                  class="inline-flex items-center whitespace-nowrap px-1.5 py-0.5 rounded text-[11px] font-medium border flex-shrink-0 mt-1"
                  :class="getStatusBadgeClass(system.status)"
                >
                  {{ getStatusText(system.status) }}
                </span>
              </div>

              <div class="flex-grow"></div>

              <div class="mt-2">
                <div class="grid grid-cols-24 lg:grid-cols-48 gap-px h-5 mb-3">
                  <div
                    v-for="(beat, index) in getDisplayHeartbeats(
                      system.heartbeats,
                    )"
                    :key="index"
                    class="rounded-sm transition-all duration-200 hover:scale-110 cursor-help relative group"
                    :class="getHeartbeatClass(beat.status)"
                    :title="formatHeartbeatTooltip(beat)"
                  >
                    <div
                      class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none"
                    >
                      {{ formatHeartbeatTooltip(beat) }}
                      <div
                        class="absolute top-full left-1/2 transform -translate-x-1/2 border-2 border-transparent border-t-gray-900"
                      ></div>
                    </div>
                  </div>
                </div>

                <div
                  class="w-full bg-gray-200/70 rounded-full h-1.5 overflow-hidden mb-2"
                >
                  <div
                    class="h-1.5 rounded-full transition-all duration-300"
                    :class="getUptimeBarClass(system.uptime24h)"
                    :style="{ width: `${(system.uptime24h || 0) * 100}%` }"
                  ></div>
                </div>

                <div class="flex justify-between text-[11px]">
                  <div class="text-gray-500 whitespace-nowrap truncate">
                    <span class="font-semibold text-gray-700">{{
                      formatUptime(system.uptime24h)
                    }}</span>
                    运行时间
                  </div>
                  <div class="text-gray-500 whitespace-nowrap truncate">
                    延迟
                    <span class="font-semibold text-gray-700">{{
                      system.latency !== null
                        ? `${system.latency}ms`
                        : t("status.unknown")
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </base-layout>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from "vue";
import { useI18n } from "vue-i18n";
import {
  ArrowPathIcon,
  QuestionMarkCircleIcon,
  ShieldCheckIcon,
  ClockIcon,
  ChartBarIcon,
} from "@heroicons/vue/24/outline";
import BaseLayout from "@/components/layout/BaseLayout.vue";
import { systemConfigService } from "@/services/systemConfigService";

const { t } = useI18n();

const loading = ref(false);
const isRefreshing = ref(false);
const heartbeatList = ref<Record<string, any[]>>({});
const uptimeList = ref<Record<string, number>>({});
const lastUpdateTime = ref("");

let refreshInterval: number;

const serviceNames = computed<Record<string, string>>(() => {
  const mapping: Record<string, string> = {};
  const services = systemConfigService.services;

  for (const [serviceName, serviceConfig] of Object.entries(services)) {
    mapping[serviceConfig.id] = serviceName;
  }

  return mapping;
});

const systemList = computed(() => {
  return Object.entries(serviceNames.value).map(([id, name]) => ({
    id,
    name,
    status: getLatestStatus(id),
    uptime24h: uptimeList.value[`${id}_24`],
    latency: getLatestPing(id),
    heartbeats: getHeartbeats(id),
  }));
});

const overallStatus = computed(() => {
  const statuses = systemList.value.map((s) => s.status);
  if (statuses.length === 0) return "unknown";

  const downCount = statuses.filter((s) => s === 0).length;
  const maintenanceCount = statuses.filter((s) => s === 3).length;
  const totalCount = statuses.length;

  if (downCount === 0 && maintenanceCount === 0) return "operational";
  if (downCount === totalCount) return "major_outage";
  if (maintenanceCount > 0) return "maintenance";
  if (downCount > totalCount / 2) return "major_outage";
  return "partial_outage";
});

const overallStatusText = computed(() => {
  switch (overallStatus.value) {
    case "operational":
      return t("status.allOperational");
    case "partial_outage":
      return t("status.partialDown");
    case "major_outage":
      return t("status.majorityDown");
    case "maintenance":
      return t("status.maintenance");
    default:
      return t("status.unknown");
  }
});

const overallStatusDotClass = computed(() => {
  switch (overallStatus.value) {
    case "operational":
      return "bg-green-500";
    case "partial_outage":
      return "bg-yellow-500";
    case "major_outage":
      return "bg-red-500";
    case "maintenance":
      return "bg-blue-500";
    default:
      return "bg-gray-400";
  }
});

const overallStatusBadgeClass = computed(() => {
  switch (overallStatus.value) {
    case "operational":
      return "bg-green-50 text-green-700 border-green-200";
    case "partial_outage":
      return "bg-yellow-50 text-yellow-700 border-yellow-200";
    case "major_outage":
      return "bg-red-50 text-red-700 border-red-200";
    case "maintenance":
      return "bg-blue-50 text-blue-700 border-blue-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
});

const operationalCount = computed(
  () => systemList.value.filter((s) => s.status === 1).length,
);

const totalSystems = computed(() => systemList.value.length);

const operationalPercentage = computed(() =>
  totalSystems.value > 0
    ? Math.round((operationalCount.value / totalSystems.value) * 100)
    : 0,
);

const averageLatency = computed(() => {
  const validLatencies = systemList.value
    .map((s) => s.latency)
    .filter(
      (latency): latency is number => latency !== null && latency !== undefined,
    );

  if (validLatencies.length === 0) return 0;
  return Math.round(
    validLatencies.reduce((a, b) => a + b, 0) / validLatencies.length,
  );
});

const averageUptime = computed(() => {
  const validUptimes = systemList.value
    .map((s) => s.uptime24h)
    .filter(
      (uptime): uptime is number => uptime !== undefined && uptime !== null,
    );

  if (validUptimes.length === 0) return "0%";
  const avg = validUptimes.reduce((a, b) => a + b, 0) / validUptimes.length;
  return `${(avg * 100).toFixed(1)}%`;
});

const fetchStatus = async () => {
  try {
    isRefreshing.value = true;
    loading.value = systemList.value.length === 0;
    const response = await fetch("/status/api/status-page/heartbeat/abdnims");
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    const data = await response.json();
    heartbeatList.value = data.heartbeatList || {};
    uptimeList.value = data.uptimeList || {};
    lastUpdateTime.value = new Date().toLocaleTimeString();
  } catch (error) {
    console.error("Error fetching status:", error);
    if (Object.keys(heartbeatList.value).length === 0) {
      setFallbackData();
    }
  } finally {
    isRefreshing.value = false;
    loading.value = false;
  }
};

const setFallbackData = () => {
  const fallbackHeartbeats = Object.keys(serviceNames.value).reduce(
    (acc, id) => {
      acc[id] = Array.from({ length: 48 }, (_, i) => ({
        status: 2,
        ping: null,
        time: new Date(Date.now() - (47 - i) * 30 * 60 * 1000).toISOString(),
      }));
      return acc;
    },
    {} as Record<string, any[]>,
  );

  const fallbackUptimes = Object.keys(serviceNames.value).reduce(
    (acc, id) => {
      acc[`${id}_24`] = 0;
      return acc;
    },
    {} as Record<string, number>,
  );

  heartbeatList.value = fallbackHeartbeats;
  uptimeList.value = fallbackUptimes;
  lastUpdateTime.value = new Date().toLocaleTimeString();
};

const getHeartbeats = (id: string) => {
  return heartbeatList.value[id] || [];
};

const getLatestStatus = (id: string): number => {
  const beats = getHeartbeats(id);
  return beats.length > 0 ? beats[beats.length - 1].status : 2;
};

const getLatestPing = (id: string): number | null => {
  const beats = getHeartbeats(id);
  return beats.length > 0 ? beats[beats.length - 1].ping : null;
};

const getDisplayHeartbeats = (heartbeats: any[]) => {
  if (heartbeats.length === 0) {
    return Array.from({ length: 48 }, (_, i) => ({
      status: 2,
      ping: null,
      time: new Date(Date.now() - (47 - i) * 30 * 60 * 1000).toISOString(),
    }));
  }
  const recentBeats = heartbeats.slice(-48);
  const missingCount = 48 - recentBeats.length;
  if (missingCount > 0) {
    const paddingBeats = Array.from({ length: missingCount }, (_, i) => ({
      status: 2,
      ping: null,
      time: new Date(
        Date.now() - (missingCount - i) * 30 * 60 * 1000,
      ).toISOString(),
    }));
    return [...paddingBeats, ...recentBeats];
  }
  return recentBeats;
};

const getStatusText = (status: number): string => {
  switch (status) {
    case 1:
      return t("status.operational");
    case 0:
      return t("status.down");
    case 3:
      return t("status.maintenance");
    default:
      return t("status.unknown");
  }
};

const getStatusDotClass = (status: number): string => {
  switch (status) {
    case 1:
      return "bg-green-500";
    case 0:
      return "bg-red-500";
    case 3:
      return "bg-blue-500";
    default:
      return "bg-gray-400";
  }
};

const getStatusBadgeClass = (status: number): string => {
  switch (status) {
    case 1:
      return "bg-green-50 text-green-700 border-green-200";
    case 0:
      return "bg-red-50 text-red-700 border-red-200";
    case 3:
      return "bg-blue-50 text-blue-700 border-blue-200";
    default:
      return "bg-gray-50 text-gray-700 border-gray-200";
  }
};

const getHeartbeatClass = (status: number): string => {
  switch (status) {
    case 1:
      return "bg-green-500";
    case 0:
      return "bg-red-500";
    case 3:
      return "bg-blue-500";
    default:
      return "bg-gray-300";
  }
};

const getUptimeBarClass = (uptime: number | undefined): string => {
  if (uptime === undefined || uptime === null) return "bg-gray-300";
  if (uptime >= 0.99) return "bg-green-500";
  if (uptime >= 0.95) return "bg-yellow-500";
  return "bg-red-500";
};

const formatUptime = (uptime: number | undefined): string => {
  if (uptime === undefined || uptime === null) return t("status.unknown");
  return `${(uptime * 100).toFixed(1)}%`;
};

const formatHeartbeatTooltip = (beat: any): string => {
  const time = new Date(beat.time).toLocaleString();
  const status = getStatusText(beat.status);
  const ping = beat.ping ? `${beat.ping}ms` : t("status.unknown");
  return `${time} - ${status} (${ping})`;
};

onMounted(() => {
  fetchStatus();
  refreshInterval = window.setInterval(fetchStatus, 30000);
});

onUnmounted(() => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
  }
});
</script>

<style scoped>
.grid-cols-48 {
  grid-template-columns: repeat(48, minmax(0, 1fr));
}

.grid-cols-24 {
  grid-template-columns: repeat(24, minmax(0, 1fr));
}

.bg-grid-pattern {
  background-image:
    linear-gradient(rgba(0, 0, 0, 0.05) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 0, 0, 0.05) 1px, transparent 1px);
  background-size: 20px 20px;
}

.group:hover .opacity-0 {
  animation: fadeInTooltip 0.15s ease-out forwards;
}

@keyframes fadeInTooltip {
  from {
    opacity: 0;
    transform: translateY(2px) translateX(-50%);
  }
  to {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
}
</style>
